// Contact profile card FIRST/LAST derivation (marker cp-couple-card-names-v1).
//
// The contact-detail profile card ("Contact profile") shows a First Name and a
// Last Name field. Historically these were derived by splitting the composed
// household `preferred_name` on whitespace: first = first token, last = the
// rest. That is correct for a single person ("Elaine Lowe" → Elaine / Lowe) but
// produces GARBAGE for a couple/household display, which carries an ampersand:
//   "Elaine & Peter Lowe"  → FIRST "Elaine",  LAST "& Peter Lowe"
//   "Alan & Karen Dorman"  → FIRST "Alan",    LAST "& Karen Dorman"
// (and, with the HTML-escaped form seen live, FIRST "Elaine &amp;" / LAST
// "&amp; Peter Lowe").
//
// Since train 1 the detail view already loads structured `contact_persons` rows
// (each with is_primary + first_name/last_name) whenever a couple is split. When
// those rows exist we derive the card's first/last from the PRIMARY person
// instead of parsing the composed name; the composed `preferred_name` stays the
// big display title unchanged. Records with NO persons keep the exact legacy
// whitespace split, byte-for-byte.
//
// Pure: no DOM, no React, no I/O — safe under the vitest `node` env that gates
// `npm test`.

import { parsePersonName } from './nameNormalize'

// Minification-proof shipped-bundle marker. The ship LIVE-verify greps the
// served JS for this literal to prove this module actually shipped.
export const DERIVE_CARD_NAMES_MARKER = 'cp-couple-card-names-v1'

/** The minimal structural shape this helper needs from a contact_persons row.
 *  Kept structural (not the api.ts `ContactPerson`) so the helper stays
 *  framework-agnostic and importable under the vitest node env. */
export interface CardNamePerson {
  first_name?: string | null
  last_name?: string | null
  full_name?: string | null
  is_primary?: number | boolean | null
}

export interface CardNames {
  first: string
  last: string
}

/** Pick the primary person: the first row flagged `is_primary`, else the first
 *  row in the list. Returns null when there are no usable rows. */
function pickPrimaryPerson(persons?: CardNamePerson[] | null): CardNamePerson | null {
  if (!persons || persons.length === 0) return null
  const primary = persons.find((p) => !!p && !!p.is_primary)
  return primary ?? persons[0] ?? null
}

/** Split a name the exact legacy way: first whitespace-delimited token is the
 *  first name, everything after it is the last name. Preserves the original
 *  inline behavior byte-for-byte (`name.split(' ')`), so no-persons contacts are
 *  unchanged. */
function legacySplit(preferredName: string | null | undefined): CardNames {
  const name = preferredName ?? ''
  return {
    first: name.split(' ').slice(0, 1).join(' '),
    last: name.split(' ').slice(1).join(' '),
  }
}

/**
 * Derive the FIRST/LAST shown on the contact profile card.
 *
 * When the record carries structured `contact_persons`, the card's first/last
 * come from the PRIMARY person (is_primary, falling back to the first row) —
 * NEVER from parsing the composed household `preferred_name`. A person row that
 * lacks structured first/last but has a `full_name` is parsed with
 * `parsePersonName` (handles "Last, First"). Records with no persons keep the
 * exact legacy whitespace split.
 *
 * @param preferredName the composed/preferred display name (unchanged as title)
 * @param persons       structured contact_persons rows, if loaded
 */
export function deriveCardNames(
  preferredName: string | null | undefined,
  persons?: CardNamePerson[] | null,
): CardNames {
  const primary = pickPrimaryPerson(persons)
  if (primary) {
    const first = (primary.first_name ?? '').trim()
    const last = (primary.last_name ?? '').trim()
    if (first || last) return { first, last }
    // Person row without structured first/last → parse its full_name. Keep the
    // card's "everything after the first token = last name" semantics by folding
    // middle + last + suffix into last.
    const parsed = parsePersonName(primary.full_name ?? '')
    if (parsed.firstName || parsed.lastName) {
      return {
        first: parsed.firstName,
        last: [parsed.middle, parsed.lastName, parsed.suffix].filter(Boolean).join(' ').trim(),
      }
    }
  }
  return legacySplit(preferredName)
}
